import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { usePerformGlobalRefresh } from "../components/header/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";

interface CancelPickedShiftsParams {
	departmentId: string;
	nurseId: number;
	requestIds: string[];
}

interface PickMergedShiftsParams {
	departmentId: string;
	shiftIds: string[];
	acceptedStartTime?: DateTime;
	acceptedEndTime?: DateTime;
	partial: boolean;
}

export const useCancelPickedShifts = () => {
	return useMutation({
		mutationFn: async (params: CancelPickedShiftsParams) => {
			const response = await backendService.cancelPickedShifts(params.departmentId, {
				nurse_id: params.nurseId,
				request_ids: params.requestIds,
			});
			return response.data;
		},
	});
};

type Params = { refreshUponSuccess?: boolean };
export const usePickMergedShifts = ({ refreshUponSuccess = false }: Params = {}) => {
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { showErrorSnackbar } = useHospitalData();
	const { t } = useTranslation();
	return useMutation({
		mutationFn: async (params: PickMergedShiftsParams) => {
			const response = await backendService.pickMergedShifts(params.departmentId, {
				shift_ids: params.shiftIds,
				accepted_start_time: params.acceptedStartTime,
				accepted_end_time: params.acceptedEndTime,
			});
			if (refreshUponSuccess) {
				try {
					await performGlobalRefresh({
						avoidRefreshFor: {
							notificationsData: true,
							nursesData: true,
						},
					});
				} catch (error) {
					showErrorSnackbar(t("error.refresh.after.pick"), error as Error);
				}
			}
			return response;
		},
		throwOnError: false,
		onError: (error) => {
			showErrorSnackbar(t("error.pick.open.shift"), {}, error);
		},
	});
};
